import { useCallback, useEffect, useState } from "react";
import { I_Alert, I_Doc, I_Down, I_LogOut, I_Refresh, I_Spark, I_Trash, I_Up, I_User, I_Users } from "./icons";
import { Ava, Spinner } from "./ui";
import { logActivity } from "../lib/activity.js";
import { fmtDate } from "../lib/format.js";
import { ROLES, ROLE_LABEL } from "../constants.js";
import { btnG, btnP, glass, heading, input, label, monoFont } from "../styles.js";
import { sb } from "../supabase.js";

const SettingsView = ({
    session,
    clients,
    properties,
    visits,
    onChanged,
    onSignOut,
    onRefreshProfile,
  }) => {
    const [busy, setBusy] = useState(false),
      [msg, setMsg] = useState(null),
      [err, setErr] = useState(null);
    const [arming, setArming] = useState(false);
    const [typed, setTyped] = useState("");
    const [wipeBusy, setWipeBusy] = useState(false);
    const [wipeMsg, setWipeMsg] = useState(null);
    const [wipeErr, setWipeErr] = useState(null);
    const [origBusy, setOrigBusy] = useState(false);
    const [origMsg, setOrigMsg] = useState(null);
    const [origErr, setOrigErr] = useState(null);
    const [users, setUsers] = useState([]);
    const [usersLoading, setUsersLoading] = useState(false);
    const [xlsxLoading, setXlsxLoading] = useState(false);
    const isAdmin = session.role === "admin";
    const canDelete = isAdmin || session.canDeleteClients;
    const clientCount = clients?.length || 0;
    const canConfirm = arming && typed.trim() === String(clientCount) && clientCount > 0;

    // ---- load users (admin only) -----------------------------------------
    const loadUsers = useCallback(async () => {
      if (!isAdmin) return;
      setUsersLoading(true);
      try {
        const { data } = await sb
          .from("profiles")
          .select("id, full_name, role, can_delete_clients, created_at")
          .order("created_at");
        setUsers(data || []);
      } catch (e) {}
      setUsersLoading(false);
    }, [isAdmin]);
    useEffect(() => {
      loadUsers();
    }, [isAdmin]);
    const toggleCanDelete = async (u) => {
      try {
        await sb
          .from("profiles")
          .update({
            can_delete_clients: !u.can_delete_clients,
          })
          .eq("id", u.id);
        await logActivity(
          session.user.id,
          "updated",
          "import",
          null,
          `${u.can_delete_clients ? "Revoked" : "Granted"} delete-clients permission for ${u.full_name}`,
        );
        loadUsers();
      } catch (e) {
        alert("Couldn't update permission: " + e.message);
      }
    };
    const setRole = async (u, next) => {
      if (u.id === session.user.id) {
        alert("You can't change your own admin role.");
        return;
      }
      if (next === u.role) return;
      if (!confirm(`Make ${u.full_name} ${ROLE_LABEL[next]}?`)) return;
      const { error } = await sb
        .from("profiles")
        .update({
          role: next,
        })
        .eq("id", u.id);
      if (error) {
        alert("Couldn't change role: " + error.message);
        return;
      }
      await logActivity(
        session.user.id,
        "updated",
        "import",
        null,
        `Changed ${u.full_name}'s role to ${ROLE_LABEL[next]}`,
      );
      loadUsers();
    };

    // ---- import (existing path: JSON file) -------------------------------
    const importClientsJson = async (file) => {
      setBusy(true);
      setMsg(null);
      setErr(null);
      try {
        const txt = await file.text();
        const data = JSON.parse(txt);
        const arr = Array.isArray(data) ? data : data.clients || data.items || [];
        if (!Array.isArray(arr) || arr.length === 0)
          throw new Error("Couldn't find any clients in that file.");
        const rows = arr.map((x, i) => ({
          code: x.code || "C" + String(i + 1).padStart(3, "0"),
          name: x.name || "Unnamed",
          phone: x.phone || x.mobile || "",
          whatsapp: x.whatsapp || "",
          email: x.email || "",
          photo: x.photo || x.image || null,
          requirement_type: x.requirement_type || x.type || x.requirement || null,
          budget_bucket: x.budget_bucket || x.budget || null,
          priority: x.priority || "Medium",
          status: x.status || "Active",
          locality_pref: x.locality_pref || x.locality || x.location || null,
          age: x.age || null,
          fellowship: x.fellowship || null,
          bhk: x.bhk || null,
          other_members: x.other_members || null,
          sanctioned: !!x.sanctioned,
          allotted: !!x.allotted,
          notes: x.notes || x.remarks || null,
          created_by: session.user.id,
        }));
        let inserted = 0;
        for (let i = 0; i < rows.length; i += 50) {
          const { error } = await sb.from("clients").insert(rows.slice(i, i + 50));
          if (error) throw error;
          inserted += Math.min(50, rows.length - i);
        }
        await logActivity(
          session.user.id,
          "imported",
          "import",
          null,
          `Imported ${inserted} clients from JSON backup`,
        );
        setMsg(`Imported ${inserted} clients successfully.`);
        onChanged && onChanged();
      } catch (e) {
        setErr(e.message || String(e));
      }
      setBusy(false);
    };

    // ---- import original client list (fetches bundled JSON) ---------------
    const importOriginalList = async () => {
      if (!isAdmin) return;
      setOrigBusy(true);
      setOrigMsg(null);
      setOrigErr(null);
      try {
        const res = await fetch("./clients-import.json");
        if (!res.ok)
          throw new Error(
            `Couldn't fetch clients-import.json (HTTP ${res.status}). Make sure the file is in the same folder as index.html on your host.`,
          );
        const data = await res.json();
        const arr = data.clients || [];
        if (!arr.length) throw new Error("clients-import.json contained no clients.");
        const rows = arr.map((x) => ({
          ...x,
          created_by: session.user.id,
        }));
        let inserted = 0;
        for (let i = 0; i < rows.length; i += 25) {
          // smaller chunks because rows include photos
          const { error } = await sb.from("clients").insert(rows.slice(i, i + 25));
          if (error) throw error;
          inserted += Math.min(25, rows.length - i);
        }
        await logActivity(
          session.user.id,
          "imported",
          "import",
          null,
          `Imported original client list (${inserted} clients)`,
        );
        setOrigMsg(`Imported ${inserted} clients from the original Excel sheet.`);
        onChanged && onChanged();
      } catch (e) {
        setOrigErr(e.message || String(e));
      }
      setOrigBusy(false);
    };

    // ---- export: JSON backup ----------------------------------------------
    const exportJson = async () => {
      const [{ data: cs }, { data: ps }, { data: vs }, { data: fs }, { data: ns }] =
        await Promise.all([
          sb.from("clients").select("*"),
          sb.from("properties").select("*"),
          sb.from("visits").select("*"),
          sb.from("followups").select("*"),
          sb.from("calendar_notes").select("*"),
        ]);
      const blob = new Blob(
        [
          JSON.stringify(
            {
              exportedAt: new Date().toISOString(),
              clients: cs,
              properties: ps,
              visits: vs,
              followups: fs,
              calendar_notes: ns,
            },
            null,
            2,
          ),
        ],
        {
          type: "application/json",
        },
      );
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `estatebook-backup-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
    };

    // ---- export: Excel (lazy-loads SheetJS) -------------------------------
    const ensureXlsx = async () => {
      if (window.ExcelJS) return window.ExcelJS;
      setXlsxLoading(true);
      await new Promise((resolve, reject) => {
        const s = document.createElement("script");
        s.src = "https://cdn.jsdelivr.net/npm/exceljs@4.4.0/dist/exceljs.min.js";
        s.onload = resolve;
        s.onerror = () => reject(new Error("Couldn't load Excel library — check your connection."));
        document.head.appendChild(s);
      });
      setXlsxLoading(false);
      return window.ExcelJS;
    };
    const exportExcel = async () => {
      if (!isAdmin) return;
      try {
        const ExcelJS = await ensureXlsx();
        const wb = new ExcelJS.Workbook();
        wb.creator = "Estatebook";
        wb.created = new Date();

        // ---- Clients sheet (with photos) ----
        const ws1 = wb.addWorksheet("Clients", {
          properties: {
            defaultRowHeight: 60,
          },
        });
        ws1.columns = [
          {
            header: "#",
            key: "num",
            width: 5,
          },
          {
            header: "Photo",
            key: "photo",
            width: 14,
          },
          {
            header: "Code",
            key: "code",
            width: 8,
          },
          {
            header: "Name",
            key: "name",
            width: 26,
          },
          {
            header: "Phone",
            key: "phone",
            width: 14,
          },
          {
            header: "WhatsApp",
            key: "wa",
            width: 14,
          },
          {
            header: "Age",
            key: "age",
            width: 7,
          },
          {
            header: "Fellowship",
            key: "fel",
            width: 14,
          },
          {
            header: "Locality/zone",
            key: "loc",
            width: 18,
          },
          {
            header: "Requirement",
            key: "req",
            width: 13,
          },
          {
            header: "BHK",
            key: "bhk",
            width: 8,
          },
          {
            header: "Budget",
            key: "bud",
            width: 10,
          },
          {
            header: "Other members",
            key: "oth",
            width: 22,
          },
          {
            header: "Priority",
            key: "pri",
            width: 9,
          },
          {
            header: "Status",
            key: "stat",
            width: 11,
          },
          {
            header: "Sanctioned",
            key: "sanc",
            width: 11,
          },
          {
            header: "Allotted",
            key: "all",
            width: 9,
          },
          {
            header: "Request date",
            key: "reqd",
            width: 13,
          },
          {
            header: "Notes",
            key: "notes",
            width: 60,
          },
        ];
        // header style
        ws1.getRow(1).font = {
          bold: true,
          size: 11,
        };
        ws1.getRow(1).alignment = {
          vertical: "middle",
          horizontal: "left",
        };
        ws1.getRow(1).height = 24;
        ws1.getRow(1).fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: {
            argb: "FFF3EFE8",
          },
        };
        // data + images
        clients.forEach((c, i) => {
          const row = ws1.addRow({
            num: i + 1,
            photo: "",
            code: c.code || "",
            name: c.name || "",
            phone: c.phone || "",
            wa: c.whatsapp || "",
            age: c.age || "",
            fel: c.fellowship || "",
            loc: c.locality_pref || "",
            req: c.requirement_type || "",
            bhk: c.bhk || "",
            bud: c.budget_bucket || "",
            oth: c.other_members || "",
            pri: c.priority || "",
            stat: c.status || "",
            sanc: c.sanctioned ? "Yes" : "No",
            all: c.allotted ? "Yes" : "No",
            reqd: c.request_date ? fmtDate(c.request_date) : "",
            notes: c.notes || "",
          });
          row.height = 64;
          row.alignment = {
            vertical: "middle",
            wrapText: true,
          };
          if (c.photo && typeof c.photo === "string" && c.photo.startsWith("data:")) {
            try {
              const m = c.photo.match(/^data:image\/(\w+);base64,(.+)$/);
              if (m) {
                const imgId = wb.addImage({
                  base64: c.photo,
                  extension: m[1] === "jpg" ? "jpeg" : m[1],
                });
                ws1.addImage(imgId, {
                  tl: {
                    col: 1.1,
                    row: row.number - 1 + 0.05,
                  },
                  ext: {
                    width: 64,
                    height: 80,
                  },
                });
              }
            } catch (err) {
              /* skip bad photos */
            }
          }
        });
        ws1.views = [
          {
            state: "frozen",
            ySplit: 1,
          },
        ];

        // ---- Properties sheet ----
        const ws2 = wb.addWorksheet("Properties");
        ws2.columns = [
          {
            header: "#",
            key: "num",
            width: 5,
          },
          {
            header: "Code",
            key: "code",
            width: 8,
          },
          {
            header: "Title",
            key: "title",
            width: 28,
          },
          {
            header: "Type",
            key: "type",
            width: 12,
          },
          {
            header: "Status",
            key: "stat",
            width: 14,
          },
          {
            header: "Locality",
            key: "loc",
            width: 16,
          },
          {
            header: "Panchayat",
            key: "pan",
            width: 14,
          },
          {
            header: "Taluk",
            key: "taluk",
            width: 12,
          },
          {
            header: "District",
            key: "dist",
            width: 12,
          },
          {
            header: "Land (cents)",
            key: "land",
            width: 12,
          },
          {
            header: "Built-up (sqft)",
            key: "sqft",
            width: 13,
          },
          {
            header: "BHK",
            key: "bhk",
            width: 7,
          },
          {
            header: "Facing",
            key: "facing",
            width: 10,
          },
          {
            header: "Price (₹L)",
            key: "price",
            width: 10,
          },
          {
            header: "Negotiable",
            key: "neg",
            width: 11,
          },
          {
            header: "Survey no",
            key: "sno",
            width: 11,
          },
          {
            header: "Block no",
            key: "bno",
            width: 11,
          },
          {
            header: "Description",
            key: "desc",
            width: 40,
          },
        ];
        ws2.getRow(1).font = {
          bold: true,
          size: 11,
        };
        ws2.getRow(1).height = 24;
        ws2.getRow(1).fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: {
            argb: "FFF3EFE8",
          },
        };
        properties.forEach((p, i) => {
          ws2.addRow({
            num: i + 1,
            code: p.code || "",
            title: p.title || "",
            type: p.type || "",
            stat: p.status || "",
            loc: p.locality || "",
            pan: p.panchayat || "",
            taluk: p.taluk || "",
            dist: p.district || "",
            land: p.land_cents || "",
            sqft: p.builtup_sqft || "",
            bhk: p.bhk || "",
            facing: p.facing || "",
            price: p.price_lakhs || "",
            neg: p.negotiable ? "Yes" : "No",
            sno: p.survey_no || "",
            bno: p.block_no || "",
            desc: p.description || "",
          });
        });
        ws2.views = [
          {
            state: "frozen",
            ySplit: 1,
          },
        ];

        // download
        const buf = await wb.xlsx.writeBuffer();
        const blob = new Blob([buf], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `estatebook-${new Date().toISOString().slice(0, 10)}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        await logActivity(
          session.user.id,
          "imported",
          "import",
          null,
          `Exported Excel (${clients.length} clients with photos, ${properties.length} properties)`,
        );
      } catch (e) {
        alert("Excel export failed: " + (e.message || e));
      }
    };

    // ---- export: printable PDF (opens print-styled view in new tab) ------
    const exportPdf = async (which) => {
      if (!isAdmin) return;
      const today = new Date().toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      });
      const escapeHtml = (s) =>
        String(s == null ? "" : s).replace(
          /[&<>"']/g,
          (c) =>
            ({
              "&": "&amp;",
              "<": "&lt;",
              ">": "&gt;",
              '"': "&quot;",
              "'": "&#39;",
            })[c],
        );
      let title, columns, rows;
      if (which === "clients") {
        title = "Client list";
        columns = [
          "#",
          "Photo",
          "Code",
          "Name",
          "Phone",
          "Locality",
          "Requirement",
          "Budget",
          "Priority",
          "Status",
          "Sanctioned",
          "Allotted",
          "Notes",
        ];
        const photoCell = (c) =>
          c.photo
            ? '<img src="' +
              String(c.photo).replace(/"/g, "&quot;") +
              '" style="width:56px;height:70px;border-radius:6px;object-fit:cover;display:block;border:1px solid #d8cfb9;"/>'
            : "";
        rows = clients.map((c, i) => [
          i + 1,
          {
            _html: photoCell(c),
          },
          c.code,
          c.name,
          c.phone,
          c.locality_pref,
          c.requirement_type,
          c.budget_bucket,
          c.priority,
          c.status,
          c.sanctioned ? "Yes" : "No",
          c.allotted ? "Yes" : "No",
          (c.notes || "").slice(0, 160),
        ]);
      } else {
        title = "Property list";
        columns = [
          "#",
          "Code",
          "Title",
          "Type",
          "Locality",
          "Land (cents)",
          "Built-up",
          "BHK",
          "Price (₹L)",
          "Status",
          "Allotted",
        ];
        rows = properties.map((p, i) => [
          i + 1,
          p.code,
          p.title,
          p.type,
          [p.locality, p.panchayat].filter(Boolean).join(", "),
          p.land_cents,
          p.builtup_sqft,
          p.bhk,
          p.price_lakhs,
          p.status,
          p.allotted ? "Yes" : "No",
        ]);
      }
      const html =
        `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${title} — Estatebook</title>
<style>
  @page { size: A4 landscape; margin: 12mm; }
  body { font-family: -apple-system, system-ui, Inter, Helvetica, Arial, sans-serif; color: #111; margin: 0; padding: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .head { display:flex; justify-content:space-between; align-items:flex-end; border-bottom: 2px solid #111; padding-bottom: 8px; margin-bottom: 14px; }
  .head h1 { font-family: 'Fraunces', Georgia, serif; font-size: 22pt; margin: 0; letter-spacing: -0.01em; }
  .head .meta { font-size: 9pt; color: #555; text-align: right; line-height: 1.4; }
  .meta strong { color: #111; }
  table { border-collapse: collapse; width: 100%; font-size: 8.5pt; }
  th { background: #f3efe8; text-align: left; padding: 6px 8px; border-bottom: 1.5px solid #111; font-weight: 600; font-size: 8pt; text-transform: uppercase; letter-spacing: 0.04em; color: #444; }
  td { padding: 5px 8px; border-bottom: 1px solid #e5e5e5; vertical-align: top; }
  tr:nth-child(even) td { background: #fafaf8; } td { height: 78px; vertical-align: middle; }
  td.num, th.num { text-align: right; font-variant-numeric: tabular-nums; }
  .footer { margin-top: 14px; font-size: 8pt; color: #888; text-align: center; }
  @media print { .noprint { display: none !important; } }
  .noprint { position: fixed; top: 12px; right: 12px; background: #111; color: white; padding: 8px 14px; border-radius: 8px; font-size: 10pt; cursor: pointer; border: 0; box-shadow: 0 4px 12px rgba(0,0,0,0.2); }
</style></head><body>
<button class="noprint" onclick="window.print()">Print / Save as PDF</button>
<div class="head">
  <div>
    <h1>${title}</h1>
    <div style="font-size:9pt;color:#666;margin-top:2px;">Estatebook · Sujesh Kappani</div>
  </div>
  <div class="meta"><div><strong>${rows.length}</strong> records</div><div>${today}</div></div>
</div>
<table>
  <thead><tr>${columns.map((c, i) => `<th class="${i === 0 ? "num" : ""}">${escapeHtml(c)}</th>`).join("")}</tr></thead>
  <tbody>${rows
    .map(
      (r) =>
        `<tr>${r
          .map((v, i) => {
            if (v && typeof v === "object" && v._html != null)
              return `<td class="${i === 0 ? "num" : ""}" style="text-align:center;vertical-align:middle">${v._html}</td>`;
            return `<td class="${i === 0 ? "num" : ""}">${escapeHtml(v == null ? "" : v)}</td>`;
          })
          .join("")}</tr>`,
    )
    .join("")}</tbody>
</table>
<div class="footer">Generated ${new Date().toLocaleString("en-IN")} · Estatebook</div>
<scr` +
        `ipt>window.addEventListener('load', () => setTimeout(() => window.print(), 350));</scr` +
        `ipt>
</body></html>`;
      const w = window.open("", "_blank");
      if (!w) {
        alert("Pop-up blocked — allow pop-ups for this site to download the PDF.");
        return;
      }
      w.document.write(html);
      w.document.close();
      await logActivity(
        session.user.id,
        "imported",
        "import",
        null,
        `Exported ${which} list to PDF (${rows.length} records)`,
      );
    };

    // ---- danger zone: bulk-delete all clients ----------------------------
    const wipeAllClients = async () => {
      if (!canConfirm) return;
      setWipeBusy(true);
      setWipeMsg(null);
      setWipeErr(null);
      try {
        await logActivity(
          session.user.id,
          "updated",
          "import",
          null,
          `Deleted all clients (${clientCount} records)`,
        );
        const { error } = await sb
          .from("clients")
          .delete()
          .neq("id", "00000000-0000-0000-0000-000000000000");
        if (error) throw error;
        setWipeMsg(
          `Deleted ${clientCount} clients. Their follow-ups were removed; visit history was kept but unlinked.`,
        );
        setArming(false);
        setTyped("");
        onChanged && onChanged();
      } catch (e) {
        setWipeErr(e.message || String(e));
      }
      setWipeBusy(false);
    };
    return (
      <div className="space-y-4">
        <div>
          <h1 className="text-4xl text-slate-100 mb-1" style={heading}>
            Settings
          </h1>
          <p className="text-sm text-slate-400">Import, export, account, admin controls.</p>
        </div>
        <div className={`${glass} rounded-2xl p-4 flex items-center gap-3 flex-wrap`}>
          <div
            className={`w-10 h-10 rounded-lg flex items-center justify-center ${isAdmin ? "bg-amber-300/20 text-amber-200" : "bg-white/[0.06] text-slate-300"}`}
          >
            {isAdmin ? <I_Spark size={18} /> : <I_User size={18} />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm text-slate-100">{session.fullName || session.user.email}</div>
            <div className="text-[11px] text-slate-400">
              {isAdmin ? "Administrator" : ROLE_LABEL[session.role] || "Agent"}
              {session.role === "power" ? " · can sanction clients" : ""}
              {!isAdmin && session.canDeleteClients ? " · can delete clients" : ""}
            </div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <button
              onClick={onRefreshProfile}
              title="Re-check your role (after admin/SQL changes)"
              className={`${btnG} text-xs py-1.5 px-2.5`}
            >
              <I_Refresh size={12} />
              Refresh role
            </button>
            <button
              onClick={() => {
                if (confirm("Reload the app? Any unsaved input will be lost.")) {
                  caches &&
                    caches.keys &&
                    caches.keys().then((ks) => ks.forEach((k) => caches.delete(k)));
                  location.reload();
                }
              }}
              title="Force-reload with a fresh copy of the app"
              className={`${btnG} text-xs py-1.5 px-2.5`}
            >
              <I_Refresh size={12} />
              Reload app
            </button>
          </div>
        </div>
        <div className={`${glass} rounded-2xl p-5 space-y-4`}>
          <div>
            <h2 className="text-lg text-slate-100 mb-1 flex items-center gap-2" style={heading}>
              <I_Down size={16} className="text-emerald-300" />
              Import clients (JSON backup)
            </h2>
            <p className="text-sm text-slate-400 mb-3">
              From a backup file or an export of the old client app.
            </p>
            <label className={`${btnG} cursor-pointer`}>
              {busy ? <Spinner size={13} /> : <I_Up size={13} />} Choose JSON file
              <input
                type="file"
                accept="application/json,.json"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) importClientsJson(f);
                }}
              />
            </label>
            {msg && (
              <div className="text-xs text-emerald-200 bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-2.5 mt-3">
                {msg}
              </div>
            )}
            {err && (
              <div className="text-xs text-rose-300 bg-rose-500/10 border border-rose-500/20 rounded-lg p-2.5 mt-3">
                {err}
              </div>
            )}
          </div>
        </div>
        {isAdmin && (
          <div className={`${glass} rounded-2xl p-5`}>
            <h2 className="text-lg text-slate-100 mb-1 flex items-center gap-2" style={heading}>
              <I_Down size={16} className="text-amber-300" />
              Import original client list{" "}
              <span className="text-[10px] uppercase tracking-wider text-amber-300/80 ml-1">
                Admin
              </span>
            </h2>
            <p className="text-sm text-slate-400 mb-3">
              Bulk-load the 175 clients from the original Excel sheet (all fields and photos
              included). One-time action — running it twice creates duplicates.
            </p>
            <button
              onClick={importOriginalList}
              disabled={origBusy}
              className={`${btnP} disabled:opacity-50`}
            >
              {origBusy ? <Spinner size={13} /> : <I_Down size={13} />}Import 175 clients
            </button>
            {origMsg && (
              <div className="text-xs text-emerald-200 bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-2.5 mt-3">
                {origMsg}
              </div>
            )}
            {origErr && (
              <div className="text-xs text-rose-300 bg-rose-500/10 border border-rose-500/20 rounded-lg p-2.5 mt-3">
                {origErr}
              </div>
            )}
          </div>
        )}
        {isAdmin && (
          <div className={`${glass} rounded-2xl p-5`}>
            <h2 className="text-lg text-slate-100 mb-1 flex items-center gap-2" style={heading}>
              <I_Up size={16} className="text-teal-300" />
              Export{" "}
              <span className="text-[10px] uppercase tracking-wider text-amber-300/80 ml-1">
                Admin
              </span>
            </h2>
            <p className="text-sm text-slate-400 mb-3">
              Printable PDF for hard-copy reference, Excel for editing, JSON for backup.
            </p>
            <div className="grid sm:grid-cols-2 gap-2">
              <button onClick={() => exportPdf("clients")} className={btnG}>
                <I_Doc size={13} className="text-rose-300" />
                PDF — Clients ({clients.length})
              </button>
              <button onClick={() => exportPdf("properties")} className={btnG}>
                <I_Doc size={13} className="text-rose-300" />
                PDF — Properties ({properties.length})
              </button>
              <button
                onClick={exportExcel}
                disabled={xlsxLoading}
                className={`${btnG} disabled:opacity-50`}
              >
                {xlsxLoading ? (
                  <Spinner size={13} />
                ) : (
                  <I_Up size={13} className="text-emerald-300" />
                )}
                Excel — Both lists
              </button>
              <button onClick={exportJson} className={btnG}>
                <I_Down size={13} />
                JSON backup (everything)
              </button>
            </div>
          </div>
        )}
        {isAdmin && (
          <div className={`${glass} rounded-2xl p-5`}>
            <h2 className="text-lg text-slate-100 mb-1 flex items-center gap-2" style={heading}>
              <I_Users size={16} className="text-sky-300" />
              User access{" "}
              <span className="text-[10px] uppercase tracking-wider text-amber-300/80 ml-1">
                Admin
              </span>
            </h2>
            <p className="text-sm text-slate-400 mb-3">
              Set each person's role — <span className="text-slate-300">Power users</span> can
              sanction (approve) clients; agents can't. You can also let non-admins delete the
              client list. Add new users in Supabase → Authentication → Users.
            </p>
            {usersLoading ? (
              <div className="text-sm text-slate-500">
                <Spinner size={14} />
              </div>
            ) : (
              <div className="space-y-2">
                {users.map((u) => (
                  <div
                    key={u.id}
                    className="p-3 rounded-lg bg-white/[0.02] border border-white/[0.04] flex items-center gap-3"
                  >
                    <Ava name={u.full_name} size="sm" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-slate-100 truncate flex items-center gap-1.5">
                        {u.full_name || "—"}
                        {u.id === session.user.id && (
                          <span className="text-[10px] text-slate-500">(you)</span>
                        )}
                      </div>
                      <div className="text-[11px] text-slate-400 flex items-center gap-2 flex-wrap">
                        <span
                          className={`px-1.5 py-0.5 rounded-full ${u.role === "admin" ? "bg-amber-300/15 text-amber-200" : u.role === "power" ? "bg-emerald-400/15 text-emerald-200" : "bg-white/[0.06] text-slate-300"}`}
                        >
                          {ROLE_LABEL[u.role] || u.role}
                        </span>
                        {u.can_delete_clients && u.role !== "admin" && (
                          <span className="px-1.5 py-0.5 rounded-full bg-rose-500/10 text-rose-200">
                            can delete
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="shrink-0 flex items-center gap-1.5">
                      {u.role !== "admin" && (
                        <button
                          onClick={() => toggleCanDelete(u)}
                          title={
                            u.can_delete_clients
                              ? "Revoke delete permission"
                              : "Allow this user to delete the client list"
                          }
                          className={`text-[11px] px-2.5 py-1.5 rounded-lg border transition ${u.can_delete_clients ? "border-rose-500/30 bg-rose-500/10 text-rose-200 hover:bg-rose-500/20" : "border-white/[0.08] bg-white/[0.04] text-slate-300 hover:bg-white/[0.08]"}`}
                        >
                          {u.can_delete_clients ? "Revoke delete" : "Allow delete"}
                        </button>
                      )}
                      {u.id !== session.user.id && (
                        <select
                          value={ROLES.includes(u.role) ? u.role : "agent"}
                          onChange={(e) => setRole(u, e.target.value)}
                          aria-label={`Role for ${u.full_name || "user"}`}
                          className="text-[11px] px-2 py-1.5 rounded-lg border border-white/[0.08] bg-white/[0.04] text-slate-300 hover:bg-white/[0.08]"
                        >
                          {ROLES.map((r) => (
                            <option key={r} value={r}>
                              {ROLE_LABEL[r]}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        <div className={`${glass} rounded-2xl p-5`}>
          <h2 className="text-lg text-slate-100 mb-1 flex items-center gap-2" style={heading}>
            <I_User size={16} className="text-slate-300" />
            Account
          </h2>
          <p className="text-sm text-slate-400 mb-3">
            Signed in as <span className="text-slate-200">{session.user.email}</span>
          </p>
          <button onClick={onSignOut} className={btnG}>
            <I_LogOut size={13} />
            Sign out
          </button>
        </div>
        {canDelete ? (
          <div className="rounded-2xl p-5 border border-rose-500/20 bg-rose-950/20 backdrop-blur-xl">
            <h2 className="text-lg text-rose-200 mb-1 flex items-center gap-2" style={heading}>
              <I_Alert size={16} className="text-rose-300" />
              Danger zone
            </h2>
            <p className="text-sm text-slate-400 mb-4">Destructive actions. Read carefully.</p>
            <div className="rounded-xl border border-rose-500/15 bg-rose-950/10 p-4">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <h3 className="text-base text-slate-100 mb-1" style={heading}>
                    Delete all clients
                  </h3>
                  <p className="text-xs text-slate-400 leading-relaxed">
                    Removes every client and their follow-ups.{" "}
                    <span className="text-slate-300">Properties and visit records are kept</span> —
                    visits will simply lose the client link. This can't be undone.{" "}
                    <strong className="text-rose-300">Export a backup first.</strong>
                  </p>
                  <div className="text-[11px] text-slate-500 mt-2" style={monoFont}>
                    {clientCount} client{clientCount === 1 ? "" : "s"} in the database right now
                  </div>
                </div>
                {!arming && (
                  <button
                    onClick={() => {
                      setArming(true);
                      setTyped("");
                      setWipeErr(null);
                      setWipeMsg(null);
                    }}
                    disabled={clientCount === 0}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border border-rose-500/30 text-rose-200 bg-rose-500/10 hover:bg-rose-500/20 disabled:opacity-40 disabled:cursor-not-allowed transition shrink-0"
                  >
                    <I_Trash size={13} />
                    Delete all clients
                  </button>
                )}
              </div>
              {arming && (
                <div className="mt-4 pt-4 border-t border-rose-500/15 space-y-3">
                  <div>
                    <label className={label}>
                      To confirm, type the exact number of clients ({clientCount})
                    </label>
                    <input
                      autoFocus={true}
                      value={typed}
                      onChange={(e) => setTyped(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && canConfirm && wipeAllClients()}
                      placeholder={`Type ${clientCount}`}
                      className={`${input} font-mono`}
                      style={monoFont}
                      inputMode="numeric"
                    />
                    {typed && !canConfirm && (
                      <div className="text-[11px] text-rose-300/80 mt-1.5">
                        That doesn't match — type the number shown above to enable the delete.
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 justify-end">
                    <button
                      onClick={() => {
                        setArming(false);
                        setTyped("");
                      }}
                      disabled={wipeBusy}
                      className={btnG}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={wipeAllClients}
                      disabled={!canConfirm || wipeBusy}
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-rose-600 hover:bg-rose-500 text-white disabled:opacity-30 disabled:cursor-not-allowed transition"
                    >
                      {wipeBusy ? <Spinner size={13} /> : <I_Trash size={13} />}Permanently delete{" "}
                      {clientCount} clients
                    </button>
                  </div>
                </div>
              )}
              {wipeMsg && (
                <div className="text-xs text-emerald-200 bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-2.5 mt-3">
                  {wipeMsg}
                </div>
              )}
              {wipeErr && (
                <div className="text-xs text-rose-300 bg-rose-500/10 border border-rose-500/20 rounded-lg p-2.5 mt-3">
                  {wipeErr}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className={`${glass} rounded-2xl p-5`}>
            <h2 className="text-lg text-slate-100 mb-1 flex items-center gap-2" style={heading}>
              <I_Alert size={16} className="text-slate-500" />
              Danger zone
            </h2>
            <p className="text-sm text-slate-500">
              Destructive actions like "Delete all clients" are admin-only. Ask an administrator to
              grant you delete permission if you need it.
            </p>
          </div>
        )}
      </div>
    );
  };

export { SettingsView };
